import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Dialog,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import sfondo from "../assets/logo-trasparente.png";
import { useTranslation } from "react-i18next";

import logo from "../assets/logo.png";

const navItems = [
  {
    label: "header.scherma",
    children: [
      {
        path: "/corsi",
        label: "header.corsi",
      },
      {
        path: "/istruttori",
        label: "header.istruttori",
      },
      {
        path: "/tornei",
        label: "header.tornei",
      },
      {
        path: "/medagliere",
        label: "header.medagliere",
      },
    ],
  },
  {
    label: "header.ricostruzione",
    children: [
      {
        path: "/rievocazione",
        label: "header.ricostruzione",
      },
      {
        path: "/pillole-di-storia",
        label: "header.articoli",
      },
    ],
  },
  {
    path: "/gallery",
    label: "header.gallery",
  },
  {
    path: "/contatti",
    label: "header.contatti",
  },
];

function Header() {
  const { t } = useTranslation();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setActiveSection(null);
  };

  const openSection = (item) => {
    setActiveSection(item);
  };

  const goBack = () => {
    setActiveSection(null);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isSectionActive = (item) => {
    if (!item.children) {
      return isActive(item.path);
    }

    return item.children.some((child) => isActive(child.path));
  };

  return (
    <header className="sticky top-0 z-50 h-20">
      <nav className="">
        <div className="header-container">
          {/* == HEADER / LOGO === */}
          <Link to="/" className="base-logo-link" onClick={closeMobileMenu}>
            <img src={logo} className="h-20" alt="Logo" />

            <span className="hidden md:inline-block base-logo-text">
              Ordine delle Lame Scaligere
            </span>
          </Link>

          {/* == MOBILE MENU BUTTON === */}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="mobile-menu-btn md:hidden"
            aria-label="Apri menu"
            aria-haspopup="dialog"
          >
            <i className="fas fa-2x fa-bars" aria-hidden="true" />
          </button>

          {/* == DESKTOP NAVIGATION === */}
          <div className="hidden md:block ">
            <ul className="nav-list">
              {navItems.map((item) => (
                <li key={item.path || item.label}>
                  {item.children ? (
                    <Popover className="relative">
                      <PopoverButton className="base-link flex items-center gap-2">
                        {t(item.label)}

                        <i
                          className="fas fa-chevron-down text-xs"
                          aria-hidden="true"
                        />
                      </PopoverButton>

                      <PopoverPanel
                        anchor="bottom start"
                        className="menu-dropdown-desktop"
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className="menu-dropdown-item-desktop"
                          >
                            {t(child.label)}
                          </Link>
                        ))}
                      </PopoverPanel>
                    </Popover>
                  ) : (
                    <Link to={item.path} className="base-link">
                      {t(item.label)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* == MOBILE NAVIGATION === */}
          <Dialog
            open={menuOpen}
            onClose={closeMobileMenu}
            className="relative z-40 md:hidden"
          >
            {/* Menu */}
            <TransitionChild
              as="div"
              enter="transition duration-200 ease-out"
              enterFrom="opacity-0 -translate-y-3"
              enterTo="opacity-100 translate-y-0"
              leave="transition duration-150 ease-in"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 -translate-y-3"
            >
              <DialogPanel className="fixed inset-x-0 bottom-0 top-20 overflow-hidden bg-primary-50 shadow-xl">
                {/* ================================================== */}
                {/* LOGO BACKGROUND / WATERMARK                        */}
                {/* ================================================== */}

                <div
                  className="pointer-events-none absolute inset-0 bg-contain bg-center bg-no-repeat opacity-10"
                  style={{
                    backgroundImage: `url(${sfondo})`,
                  }}
                  aria-hidden="true"
                />

                {/* ================================================== */}
                {/* PANELS CONTAINER                                    */}
                {/* ================================================== */}

                <div
                  className="relative z-10 flex h-full w-[200%] transition-transform duration-300 ease-in-out"
                  style={{
                    transform:
                      activeSection === null
                        ? "translateX(0)"
                        : "translateX(-50%)",
                  }}
                >
                  {/* ================================================== */}
                  {/* MAIN MENU                                          */}
                  {/* ================================================== */}

                  <div className="h-full w-1/2 shrink-0 overflow-y-auto px-6 py-6">
                    <ul className="divide-y divide-gray-500">
                      {navItems.map((item) => {
                        const active = isSectionActive(item);

                        return (
                          <li key={item.path || item.label}>
                            {item.children ? (
                              <button
                                type="button"
                                onClick={() => openSection(item)}
                                className={`flex min-h-16 w-full items-center justify-between py-4 text-left text-lg transition-colors ${
                                  active ? "font-semibold" : ""
                                }`}
                              >
                                <span>{t(item.label)}</span>

                                <i
                                  className="fas fa-chevron-right text-sm"
                                  aria-hidden="true"
                                />
                              </button>
                            ) : (
                              <Link
                                to={item.path}
                                onClick={closeMobileMenu}
                                className={`flex min-h-16 items-center py-4 text-lg transition-colors ${
                                  active ? "font-semibold" : ""
                                }`}
                              >
                                {t(item.label)}
                              </Link>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {/* ================================================== */}
                  {/* SUBMENU                                             */}
                  {/* ================================================== */}

                  <div className="h-full w-1/2 shrink-0 overflow-y-auto px-6 py-6">
                    {activeSection && (
                      <>
                        {/* Submenu header */}
                        <div className="mb-2 border-b border-gray-500">
                          <button
                            type="button"
                            onClick={goBack}
                            className="flex min-h-16 w-full items-center gap-4 py-4 text-left text-lg font-semibold"
                          >
                            <i
                              className="fas fa-chevron-left text-sm"
                              aria-hidden="true"
                            />

                            <span>{t(activeSection.label)}</span>
                          </button>
                        </div>

                        {/* Submenu items */}
                        <ul className="divide-y divide-gray-500">
                          {activeSection.children.map((child) => {
                            const active = isActive(child.path);

                            return (
                              <li key={child.path}>
                                <Link
                                  to={child.path}
                                  onClick={closeMobileMenu}
                                  className={`flex min-h-16 items-center py-4 text-lg transition-colors ${
                                    active ? "font-semibold" : ""
                                  }`}
                                >
                                  {t(child.label)}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </Dialog>
        </div>
      </nav>
    </header>
  );
}

export default Header;
